import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import { Skeleton } from '@/components/ui/skeleton'

// Company-wide jobs one person owns, such as approving store customers' tax-exempt forms (USA-1504).

type StaffFunctionOwner = components['schemas']['StaffFunctionOwnerRead']

const KEY = ['staff-function-owners'] as const

function useStaffFunctionOwners() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff-function-owners')
      if (error) throw error
      return data
    },
  })
}

function useUpdateStaffFunctionOwner() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ fn, userId }: { fn: StaffFunctionOwner['function']; userId: number | null }) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/staff-function-owners/{function}', {
        params: { path: { function: fn } },
        body: { user_id: userId },
      })
      if (error) throw error
      return data
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<StaffFunctionOwner[]>(KEY, (current) =>
        current?.map((owner) => (owner.function === updated.function ? updated : owner)),
      )
    },
  })
}

export function StaffFunctionOwners() {
  const owners = useStaffFunctionOwners()
  const update = useUpdateStaffFunctionOwner()
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">Other roles</h2>
        <p className="text-sm text-muted-foreground">One person owns each of these across all projects.</p>
      </div>
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="divide-y">
          {owners.isLoading ? (
            <div className="grid gap-4 px-5 py-4 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-6">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full max-w-64 rounded-full" />
            </div>
          ) : owners.isError ? (
            <p className="px-5 py-4 text-sm font-medium">These roles could not be loaded.</p>
          ) : (
            owners.data?.map((owner) => (
              <div
                className="grid gap-2 px-5 py-4 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-6"
                key={owner.function}
              >
                <h3 className="text-sm font-semibold">{owner.label}</h3>
                <StaffAssignmentPicker
                  className="w-full max-w-none justify-start"
                  currentUser={owner.user ?? null}
                  disabled={update.isPending}
                  label={owner.label}
                  onChange={(user) =>
                    update.mutate(
                      { fn: owner.function, userId: user?.id ?? null },
                      {
                        onSuccess: () => toast.success(`${owner.label} updated`),
                        onError: () => toast.error(`Could not update ${owner.label}`),
                      },
                    )
                  }
                  pending={update.isPending && update.variables?.fn === owner.function}
                  showName
                />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  )
}
